"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TranslationsProvider = void 0;
var _fetching = require("@ori/fetching");
var _react = require("react");
var _reactIntl = require("react-intl");
var _constants = require("../../internals/constants");
var _logger = require("../../logger");
var _utils = require("../../utils");
var _translationsContext = require("./translationsContext");
var _utils2 = require("./utils");
var _jsxRuntime = require("react/jsx-runtime");
const logger = _logger.mainLogger.createChild('TranslationsProvider');
const handleIntlProviderError = err => {
  if (err.code === _reactIntl.ReactIntlErrorCode.MISSING_DATA) {
    logger.info(err.message);
    return;
  }
  if (err.code === _reactIntl.ReactIntlErrorCode.FORMAT_ERROR) {
    logger.warn(err.message);
    return;
  }
  if (err.code === _reactIntl.ReactIntlErrorCode.MISSING_TRANSLATION && err.descriptor?.id?.includes(_constants.DYNAMIC_TRANSLATION_PREFIX)) {
    return;
  }
  throw err;
};

/**
 * Provider which provides translations context and internationalization for its children.
 * @returns Rendered component with translations context and internationalization provided.
 */
const TranslationsProvider = ({
  translationsWithNamespaces,
  defaultTranslationsWithNamespaces,
  currentLanguage,
  language,
  clientFetchConfiguration,
  children
}) => {
  const usedLanguage = language ?? currentLanguage ?? '';
  const fallbackLocale = usedLanguage ? usedLanguage.slice(0, 2) : 'en';
  /**
   * Determines whenever data is needed to be fetch on client or not.
   */
  const shouldFetch = (0, _react.useMemo)(() => !translationsWithNamespaces && clientFetchConfiguration && (!!clientFetchConfiguration.baseUrl || typeof window !== 'undefined'), [clientFetchConfiguration, translationsWithNamespaces]);
  /**
   * Fetches localization data in standalone apps using SWR with provided options.
   */
  const {
    data: localizationData
  } = (0, _fetching.useSWR)(shouldFetch ? [usedLanguage, clientFetchConfiguration?.tenant, defaultTranslationsWithNamespaces] : null, async () => clientFetchConfiguration ? (0, _utils.fetchTranslationsWithNamespaces)({
    clientFetchConfiguration: {
      baseUrl: clientFetchConfiguration.baseUrl ?? window.location.origin,
      tenant: clientFetchConfiguration.tenant
    },
    language: usedLanguage,
    defaultTranslationsWithNamespaces,
    onError: clientFetchConfiguration.onError
  }) : null, {
    fallbackData: {
      translationsWithNamespaces: translationsWithNamespaces ?? defaultTranslationsWithNamespaces
    },
    suspense: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    revalidateOnMount: true
  });
  /**
   * Determines the data to use for the translations context.
   */
  const data = localizationData?.translationsWithNamespaces ?? translationsWithNamespaces;
  const showTranslationKeys = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('_showTranslationKeys');
  const contextValue = (0, _react.useMemo)(() => ({
    translationsWithNamespaces: showTranslationKeys && data ? (0, _utils2.mapNamespacedTranslations)(data, _utils2.mapTranslationsToKeys) : data,
    defaultTranslationsWithNamespaces: showTranslationKeys ? (0, _utils2.mapNamespacedTranslations)(defaultTranslationsWithNamespaces, _utils2.mapTranslationsToKeys) : defaultTranslationsWithNamespaces,
    currentLanguage: usedLanguage,
    language: usedLanguage,
    clientFetchConfiguration: clientFetchConfiguration ?? undefined,
    showTranslationKeys
  }), [data, defaultTranslationsWithNamespaces, usedLanguage, clientFetchConfiguration, showTranslationKeys]);
  const reactIntlData = (0, _utils2.flattenAndConcatenateKeysWithNamespace)(data ? showTranslationKeys ? (0, _utils2.mapNamespacedTranslations)(data, _utils2.mapTranslationsToKeys) : data : {});
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_translationsContext.translationsContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.IntlProvider, {
      locale: usedLanguage,
      defaultLocale: fallbackLocale,
      messages: reactIntlData,
      onError: handleIntlProviderError,
      children: children
    })
  });
};
exports.TranslationsProvider = TranslationsProvider;
//# sourceMappingURL=TranslationsProvider.js.map